import {
  optionGroupForm,
  Quantity,
} from '@/pages/menu-managements/option-groups/service';
import { FormError, validBlank, validMaxStr } from '@/validate';

export type OptionGroupFormError = {
  titleJa: FormError;
  memo: FormError;
  quantity: {
    min: FormError;
    max: FormError;
    maxSameOption: FormError;
  };
};

const MAX_80 = 80;

export const validateTitleJa = (
  title: string,
  error: OptionGroupFormError
): OptionGroupFormError => {
  error.titleJa.isTouched = true;
  const label = optionGroupForm.title.label;
  const isMax = validMaxStr.check(title, MAX_80);
  const isBlank = validBlank.check(title);
  if (isBlank) error.titleJa.message = validBlank.message(label);
  if (isMax) error.titleJa.message = validMaxStr.message(label, MAX_80);
  error.titleJa.isError = isBlank || isMax;
  return error;
};

const MAX_2000 = 2000;
export const validateMemo = (
  memo: string,
  error: OptionGroupFormError
): OptionGroupFormError => {
  error.memo.isTouched = true;
  const label = optionGroupForm.memo.label;
  const isMax = validMaxStr.check(memo, MAX_2000);
  if (isMax) error.memo.message = validMaxStr.message(label, MAX_2000);
  error.memo.isError = isMax;
  return error;
};

const maxLabel = optionGroupForm.max.label;
const minLabel = optionGroupForm.min.label;
const maxSameOptionLabel = optionGroupForm.maxSameOption.label;

export const validateLessMinItemLength = (
  min: number | '',
  maxSameOption: number | '',
  selectedItemLength: number,
  error: OptionGroupFormError
): { isLessMinItemLength: boolean; error: OptionGroupFormError } => {
  if (maxSameOption === '') return { isLessMinItemLength: false, error };
  if (selectedItemLength === 0) return { isLessMinItemLength: false, error };
  // @ts-ignore TS2365
  const isLessMinItemLength = selectedItemLength * maxSameOption < min;
  if (isLessMinItemLength) {
    error.quantity.min.message = `「${minLabel}」は、「トッピンググループに紐づく商品数」×「${maxSameOptionLabel}」以下の数字を指定して下さい。`;
  }
  return { isLessMinItemLength, error };
};

export const validateQuantityMin = (
  quantity: Quantity,
  selectedItemLength: number,
  error: OptionGroupFormError
): OptionGroupFormError => {
  error.quantity.min.isTouched = true;
  const { min, max, max_same_option } = quantity;

  // maxのバリデーション
  const isMinThanMax = min !== '' && max !== '' && min > max;
  if (isMinThanMax) {
    error.quantity.max.message = `「${maxLabel}」は「${minLabel}」以上の数字を指定して下さい。`;
  }
  error.quantity.max.isError = isMinThanMax;

  // minのバリデーション
  const isBlank = min === '' && validBlank.check(min);
  if (isBlank) error.quantity.min.message = validBlank.message(minLabel);
  const { isLessMinItemLength, error: newError } = validateLessMinItemLength(
    min,
    max_same_option,
    selectedItemLength,
    error
  );
  error = newError;
  error.quantity.min.isError = isBlank || isLessMinItemLength;
  return error;
};

export const validateQuantityMax = (
  quantity: Quantity,
  error: OptionGroupFormError
): OptionGroupFormError => {
  const maxSameOptionLabel = optionGroupForm.maxSameOption.label;
  const { min, max, max_same_option } = quantity;
  // maxの処理
  error.quantity.max.isTouched = true;
  const isMinThanMax = min !== '' && max !== '' && min > max;
  if (isMinThanMax) {
    error.quantity.max.message = `「${maxLabel}」は「${minLabel}」以上の数字を指定して下さい。`;
  }
  error.quantity.max.isError = isMinThanMax;

  const isMaxThanMaxSameOption =
    max_same_option !== '' && max !== '' && max_same_option > max;
  if (isMaxThanMaxSameOption) {
    error.quantity.max.message = `「${maxLabel}」は「${maxSameOptionLabel}」以上の数字を指定して下さい。`;
  }
  error.quantity.max.isError = isMinThanMax || isMaxThanMaxSameOption;

  return error;
};

export const validateQuantityMaxSameOption = (
  quantity: Quantity,
  selectedItemLength: number,
  error: OptionGroupFormError
): OptionGroupFormError => {
  error.quantity.maxSameOption.isTouched = true;
  const { min, max, max_same_option } = quantity;
  // minの処理
  const { isLessMinItemLength, error: newError } = validateLessMinItemLength(
    min,
    max_same_option,
    selectedItemLength,
    error
  );
  error = newError;
  error.quantity.min.isError = isLessMinItemLength;
  // maxの処理
  const isMaxThanMaxSameOption =
    max_same_option !== '' && max !== '' && max_same_option > max;
  if (isMaxThanMaxSameOption) {
    error.quantity.max.message = `「${maxLabel}」は「${maxSameOptionLabel}」以上の数字を指定して下さい。`;
  }
  error.quantity.max.isError = isMaxThanMaxSameOption;

  // max_same_optionの処理
  const MIN_NUM = 1;
  const isMInMaxSameOption =
    max_same_option !== '' && max_same_option < MIN_NUM;
  if (isMInMaxSameOption) {
    error.quantity.maxSameOption.message = `「${maxSameOptionLabel}」は ${MIN_NUM}以上の数字を指定して下さい。`;
  }
  error.quantity.maxSameOption.isError = isMInMaxSameOption;
  return error;
};
